import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Aperture,
  ArrowDownToLine,
  ArrowUpToLine,
  Blend,
  MoveVertical,
  PenLine,
  SquareRoundCorner,
} from 'lucide-react';

import CommonSlider from '../../../../../components/common-slider/CommonSlider';
import { ARROW_HEAD_RATIO, SHAPE_KINDS } from '../../../../config';
import { useMediaStudio } from '../../../../context/MediaStudioContext';
import ColorField from '../color-field/ColorField';
import InspectorSection from '../inspector-section/InspectorSection';
import ToolButton from '../tool-button/ToolButton';
import ToolButtonGroup from '../tool-button/ToolButtonGroup';

function ShapeSection({ element }) {
  const { t } = useTranslation('mediaStudio');
  const { editor } = useMediaStudio();
  const docShortSide = Math.min(editor.width, editor.height);
  const changeLive = (changes) => editor.change(element.id, changes, { live: true });
  const change = (changes) => editor.change(element.id, changes);
  const resetLabel = t('resetSlider');
  const kind = element.kind;
  const isBox = kind === SHAPE_KINDS.RECT || kind === SHAPE_KINDS.ELLIPSE;
  const isLine = kind === SHAPE_KINDS.LINE || kind === SHAPE_KINDS.ARROW;
  const maxStroke = Math.max(4, Math.round(docShortSide * 0.05));
  const { shadow } = element;
  const setShadow = (changes, live = true) => (live ? changeLive : change)({ shadow: changes });

  return (
    <InspectorSection title={t('shapeSection')}>
      {isBox ? (
        <>
          <ColorField
            label={t('shapeFill')}
            value={element.fill}
            enabled={element.fillEnabled !== false}
            onToggle={(fillEnabled) => change({ fillEnabled })}
            onChange={(fill) => changeLive({ fill })}
            onDone={editor.endGesture}
          />
          <ColorField
            label={t('shapeOutline')}
            value={element.stroke}
            enabled={Boolean(element.strokeEnabled)}
            onToggle={(strokeEnabled) => change({ strokeEnabled })}
            onChange={(stroke) => changeLive({ stroke })}
            onDone={editor.endGesture}
          />
          {element.strokeEnabled ? (
            <CommonSlider
              label={t('effectThickness')}
              icon={PenLine}
              min={1}
              max={maxStroke}
              value={element.strokeWidth}
              resetValue={Math.max(2, Math.round(docShortSide * 0.01))}
              resetLabel={resetLabel}
              onChange={(strokeWidth) => changeLive({ strokeWidth })}
              onCommit={editor.endGesture}
            />
          ) : null}
          {kind === SHAPE_KINDS.RECT ? (
            <CommonSlider
              label={t('effectRadius')}
              icon={SquareRoundCorner}
              min={0}
              max={Math.max(1, Math.round(Math.min(element.width, element.height) / 2))}
              value={element.cornerRadius || 0}
              resetValue={0}
              resetLabel={resetLabel}
              onChange={(cornerRadius) => changeLive({ cornerRadius })}
              onCommit={editor.endGesture}
            />
          ) : null}
        </>
      ) : null}

      {isLine ? (
        <>
          <ColorField
            label={t('shapeColor')}
            value={element.fill}
            onChange={(fill) => changeLive({ fill })}
            onDone={editor.endGesture}
          />
          <CommonSlider
            label={t('effectThickness')}
            icon={PenLine}
            min={1}
            max={maxStroke}
            value={Math.round(kind === SHAPE_KINDS.ARROW ? element.height / ARROW_HEAD_RATIO : element.height)}
            resetValue={Math.max(2, Math.round(docShortSide * 0.008))}
            resetLabel={resetLabel}
            onChange={(thickness) =>
              changeLive({ height: kind === SHAPE_KINDS.ARROW ? thickness * ARROW_HEAD_RATIO : thickness })
            }
            onCommit={editor.endGesture}
          />
        </>
      ) : null}

      {kind === SHAPE_KINDS.FADE ? (
        <>
          <ColorField
            label={t('shapeColor')}
            value={element.fill}
            onChange={(fill) => changeLive({ fill })}
            onDone={editor.endGesture}
          />
          <ToolButtonGroup>
            <ToolButton
              title={t('shapeFadeFromBottom')}
              isActive={element.direction !== 'down'}
              onClick={() => change({ direction: 'up' })}>
              <ArrowUpToLine size={16} />
            </ToolButton>
            <ToolButton
              title={t('shapeFadeFromTop')}
              isActive={element.direction === 'down'}
              onClick={() => change({ direction: 'down' })}>
              <ArrowDownToLine size={16} />
            </ToolButton>
          </ToolButtonGroup>
        </>
      ) : null}

      <CommonSlider
        label={t('shapeOpacity')}
        icon={Blend}
        min={0}
        max={100}
        value={Math.round((element.opacity ?? 1) * 100)}
        resetValue={100}
        resetLabel={resetLabel}
        valueFormatter={(value) => `${value}%`}
        onChange={(value) => changeLive({ opacity: value / 100 })}
        onCommit={editor.endGesture}
      />
      <CommonSlider
        label={t('shapeBlur')}
        icon={Aperture}
        min={0}
        max={Math.max(10, Math.round(docShortSide * 0.05))}
        value={element.blur || 0}
        resetValue={0}
        resetLabel={resetLabel}
        onChange={(blur) => changeLive({ blur })}
        onCommit={editor.endGesture}
      />

      <ColorField
        label={t('shapeShadow')}
        value={shadow.color}
        enabled={Boolean(shadow.enabled)}
        onToggle={(enabled) =>
          setShadow(
            enabled
              ? {
                  enabled,
                  blur: shadow.enabled ? shadow.blur : Math.round(docShortSide * 0.02),
                  offsetY: shadow.enabled ? shadow.offsetY : Math.round(docShortSide * 0.008),
                }
              : { enabled },
            false,
          )
        }
        onChange={(color) => setShadow({ color })}
        onDone={editor.endGesture}
      />
      {shadow.enabled ? (
        <>
          <CommonSlider
            label={t('effectBlur')}
            icon={Aperture}
            min={0}
            max={Math.max(10, Math.round(docShortSide * 0.06))}
            value={shadow.blur || 0}
            resetValue={Math.round(docShortSide * 0.02)}
            resetLabel={resetLabel}
            onChange={(blur) => setShadow({ blur })}
            onCommit={editor.endGesture}
          />
          <CommonSlider
            label={t('shapeShadowDistance')}
            icon={MoveVertical}
            min={0}
            max={Math.max(10, Math.round(docShortSide * 0.05))}
            value={shadow.offsetY || 0}
            resetValue={Math.round(docShortSide * 0.008)}
            resetLabel={resetLabel}
            onChange={(offsetY) => setShadow({ offsetY })}
            onCommit={editor.endGesture}
          />
          <CommonSlider
            label={t('opacity')}
            icon={Blend}
            min={0}
            max={100}
            value={Math.round((shadow.opacity ?? 1) * 100)}
            resetValue={35}
            resetLabel={resetLabel}
            valueFormatter={(value) => `${value}%`}
            onChange={(value) => setShadow({ opacity: value / 100 })}
            onCommit={editor.endGesture}
          />
        </>
      ) : null}
    </InspectorSection>
  );
}

export default ShapeSection;
